<template>
    <div class="flex-1 flex flex-col overflow-hidden min-h-0">
        <div class="shrink-0 bg-surface-raised border-b border-border px-6 py-4">
            <div class="max-w-3xl mx-auto">
                <BasePageTitle no-border title="IMPACT Leader Code Self-Reflection" subtitle="Take a few minutes to reflect on your leadership behaviors." />
            </div>
        </div>

        <div class="flex-1 overflow-y-auto">
            <Transition name="fade" mode="out-in">
                <div v-if="phase === 'instructions'" key="instructions" class="max-w-3xl mx-auto px-6 py-10">
                    <div class="bg-surface-raised rounded-2xl border border-border shadow-sm p-10">
                        <div class="flex items-center gap-3 mb-5">
                            <div class="w-10 h-10 rounded-xl bg-surface-sunken flex items-center justify-center shrink-0">
                                <i class="bi bi-list-check text-ink text-lg" aria-hidden="true"></i>
                            </div>
                            <!-- FIXME: Consider extracting assessment section headers to a shared BasePageHeader component -->
                            <h2 class="title-body-bold text-ink">How This Works</h2>
                        </div>
                        <p class="body text-ink mb-4">
                            This self-reflection is grounded on the IMPACT Leader Code – that reflects our 3 commitments and 10 behaviors to support our Mission-Based culture
                            reset.
                        </p>
                        <p class="body text-ink mb-4">
                            We invite you to consider how consistently the behavior shows up in your day-to-day leadership using a rating scale from 1 (not yet) to 5 (always).
                            These reflections are for your own development. Once you complete the self-reflection you will be able to start coaching with Nadia to take it further.
                        </p>
                        <p class="body text-ink">Let's take a look at an example first.</p>
                    </div>
                </div>

                <div v-else-if="phase === 'example'" key="example" class="max-w-3xl mx-auto px-6 py-10 space-y-5">
                    <div class="bg-surface-raised rounded-2xl border border-border shadow-sm p-10">
                        <div class="flex items-center gap-3 mb-5">
                            <div class="w-10 h-10 rounded-xl bg-surface-sunken flex items-center justify-center shrink-0">
                                <i class="bi bi-eye text-ink text-lg" aria-hidden="true"></i>
                            </div>
                            <h2 class="title-body-bold text-ink">Example Question</h2>
                        </div>
                        <p class="body-small text-ink-muted mb-6">
                            You will be presented a series of behaviors on which you will be asked to reflect. Here is what a question looks like:
                        </p>
                        <div class="bg-surface-sunken rounded-xl border border-border p-5 mb-6">
                            <div class="flex items-start justify-between gap-4 mb-3">
                                <div class="flex-1">
                                    <!-- One-off: 12px font-bold tracking-widest eyebrow; no exact utility match -->
                                    <p class="details font-bold tracking-widest text-ink uppercase mb-1">Invent the Future</p>
                                    <h3 class="body-bold text-ink">I invent the future and create innovative, differentiated, and competitive solutions for our customers.</h3>
                                </div>
                                <div class="shrink-0 flex flex-col items-center justify-center w-16 h-16 rounded-xl bg-border text-ink-muted">
                                    <span class="title-body-bold leading-none">—</span>
                                </div>
                            </div>
                            <div class="mt-3">
                                <div class="h-1 rounded-full bg-border w-full mb-2"></div>
                                <div class="flex justify-between px-[10px]">
                                    <span v-for="n in 5" :key="n" class="text-[10px] text-ink-muted font-semibold">{{ n }} · {{ rating_labels[String(n)] }}</span>
                                </div>
                            </div>
                        </div>
                        <p class="body-small-bold text-ink mb-3">Rating scale (applies to all questions):</p>
                        <div class="divide-y divide-border">
                            <div v-for="n in 5" :key="n" class="flex gap-3 py-2.5">
                                <div class="shrink-0 w-7 h-7 rounded-full bg-surface-sunken text-ink details flex items-center justify-center">
                                    {{ n }}
                                </div>
                                <div>
                                    <span class="body-small-bold text-ink">{{ rating_labels[String(n)] }}</span>
                                    <span class="body-small text-ink-muted"> — {{ rating_descriptions[String(n)] }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
        </div>

        <div class="shrink-0 bg-surface-raised border-t border-border px-6 py-4">
            <div class="max-w-3xl mx-auto flex items-center justify-between">
                <BaseButton v-if="phase !== 'instructions'" theme="tertiary" :disabled="isStartingAssessment" @click="emit('intro-prev')"> ← Back </BaseButton>
                <div v-else></div>
                <BaseButton theme="primary" :disabled="isStartingAssessment" @click="emit('intro-next')">
                    {{ phase === "example" ? (isStartingAssessment ? "Starting..." : "Start Self-Reflection →") : "Continue →" }}
                </BaseButton>
            </div>
            <div v-if="startError" role="alert" class="max-w-3xl mx-auto mt-2 details text-notification-warning-text">
                {{ startError }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BasePageTitle from "~vue/components/BasePageTitle.vue";

type IntroPhase = "instructions" | "example";

interface Props {
    phase: IntroPhase;
    isStartingAssessment: boolean;
    startError: string | null;
    rating_labels: Record<string, string>;
    rating_descriptions: Record<string, string>;
}

defineProps<Props>();

const emit = defineEmits<{
    (e: "intro-prev"): void;
    (e: "intro-next"): void;
}>();
</script>
